'use client'

import { useEffect, useState } from 'react'
import Image, { ImageProps } from 'next/image'
import { cn } from 'oddle-shop-monorepo.design-system'

interface ImageWithLoadingProps extends ImageProps {
  blurDataURL?: string
  blurImageClassName?: string
}

// Global cache to track loaded images
const imageLoadCache = new Set<string>()

const ImageWithLoading = ({
  width,
  height,
  className,
  src,
  blurDataURL,
  blurImageClassName,
  ...props
}: ImageWithLoadingProps) => {
  const imageKey = typeof src === 'string' ? src : ''
  const isImageCached = imageLoadCache.has(imageKey)

  const [isLoading, setIsLoading] = useState(!isImageCached)

  // Check if image is already in browser cache on mount
  useEffect(() => {
    if (isImageCached) {
      setIsLoading(false)
    }
  }, [isImageCached])

  return (
    <div
      className="relative flex h-full w-full overflow-hidden"
      style={props.sizes === '100vw' ? {} : { width, height }}
    >
      {/* Skeleton fallback when no blur data available */}
      {isLoading && !blurDataURL && (
        <div
          role="status"
          className="absolute inset-0 h-full w-full animate-pulse"
        >
          <div className="h-full w-full rounded-2xl bg-gray-200 dark:bg-gray-300" />
        </div>
      )}

      <div className="relative flex h-full w-full">
        {/* Blur placeholder image */}
        {blurDataURL && (
          <Image
            src={blurDataURL}
            width={width}
            height={height}
            className={cn(
              'absolute inset-0 h-full w-full object-cover transition-opacity duration-500',
              blurImageClassName
            )}
            priority
            {...props}
          />
        )}
        {!isLoading && (
          <div
            className={cn(
              'absolute z-[1] h-full w-full bg-[#00000066] transition-opacity delay-100 duration-500',
              blurImageClassName
            )}
          >
            <div
              className={cn(
                'absolute inset-0 backdrop-blur-sm',
                blurImageClassName
              )}
            />
          </div>
        )}

        {/* Main high-resolution image */}
        <Image
          width={width}
          height={height}
          src={src}
          onLoad={() => {
            setIsLoading(false)
            imageLoadCache.add(imageKey) // Cache this image as loaded
          }}
          className={cn(
            'z-[2] transition-all duration-500 ease-out',
            isLoading
              ? blurDataURL
                ? 'opacity-0 blur-sm'
                : 'opacity-0'
              : 'opacity-100 blur-0',
            className
          )}
          placeholder={blurDataURL ? 'blur' : 'empty'}
          blurDataURL={blurDataURL}
          {...props}
        />
      </div>
    </div>
  )
}

export default ImageWithLoading
